import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { 
  Plus, Edit, Trash2, Save, RefreshCw, Globe, MapPin, Sparkles, 
  Eye, ExternalLink, Search, CheckCircle, XCircle, Wand2, Zap,
  Check, X, AlertCircle
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

// Predefined services and cities
const SERVICES = [
  'Création de logo',
  'Site web',
  'Charte graphique',
  'Identité visuelle',
  'Vidéo corporate',
  'Community management',
  'Référencement SEO',
  'Publicité Google Ads',
  'Brochure commerciale',
  'Motion design',
  'Web design'
];

// Top 10 villes autour d'Istres + grandes villes régionales
const CITIES = [
  // Villes proches d'Istres (rayon 30km)
  'Istres',
  'Fos-sur-Mer',
  'Miramas',
  'Port-de-Bouc',
  'Martigues',
  'Saint-Mitre-les-Remparts',
  'Grans',
  'Salon-de-Provence',
  'Arles',
  'Marignane',
  // Grandes villes régionales
  'Marseille',
  'Aix-en-Provence',
  'Avignon',
  'Nîmes',
  'Montpellier',
  // Grandes villes nationales
  'Paris',
  'Lyon',
  'Nice',
  'Toulon',
  'Cannes'
];

const CMSSeoGeneratorPage = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0, status: '' });
  
  // Multi-selection states
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [customService, setCustomService] = useState('');
  const [customCity, setCustomCity] = useState('');
  
  const [previewModal, setPreviewModal] = useState({ open: false, page: null });
  const [editModal, setEditModal] = useState({ open: false, page: null });
  const [generationResults, setGenerationResults] = useState({ open: false, results: [] });

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      const res = await api.get('/cms/seo-pages');
      setPages(res.data || []);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (service) => {
    setSelectedServices(prev => 
      prev.includes(service) 
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
  };

  const toggleCity = (city) => {
    setSelectedCities(prev => 
      prev.includes(city) 
        ? prev.filter(c => c !== city)
        : [...prev, city]
    );
  };

  const selectAllServices = () => setSelectedServices([...SERVICES]);
  const deselectAllServices = () => setSelectedServices([]);
  const selectAllCities = () => setSelectedCities([...CITIES]);
  const deselectAllCities = () => setSelectedCities([]);

  const addCustomService = () => {
    if (customService && !selectedServices.includes(customService) && !SERVICES.includes(customService)) {
      setSelectedServices(prev => [...prev, customService]);
      setCustomService('');
    }
  };

  const addCustomCity = () => {
    if (customCity && !selectedCities.includes(customCity) && !CITIES.includes(customCity)) {
      setSelectedCities(prev => [...prev, customCity]);
      setCustomCity('');
    }
  };

  const generatePages = async () => {
    const services = selectedServices.length > 0 ? selectedServices : [];
    const cities = selectedCities.length > 0 ? selectedCities : [];

    if (services.length === 0 || cities.length === 0) {
      toast.error('Veuillez sélectionner au moins un service et une ville');
      return;
    }

    // Calculate combinations
    const combinations = [];
    for (const service of services) {
      for (const city of cities) {
        // Check if page already exists
        const exists = pages.some(p => p.service === service && p.city === city);
        if (!exists) {
          combinations.push({ service, city });
        }
      }
    }

    if (combinations.length === 0) {
      toast.info('Toutes les combinaisons sélectionnées existent déjà');
      return;
    }

    setGenerating(true);
    setGenerationProgress({ current: 0, total: combinations.length, status: 'Démarrage...' });
    
    const results = [];
    
    for (let i = 0; i < combinations.length; i++) {
      const combo = combinations[i];
      setGenerationProgress({ 
        current: i + 1, 
        total: combinations.length, 
        status: `${combo.service} à ${combo.city}...` 
      });
      
      try {
        const res = await api.post('/cms/seo-pages/generate', { 
          service: combo.service, 
          city: combo.city 
        });
        results.push({ ...combo, success: true, page: res.data.page });
      } catch (error) {
        results.push({ ...combo, success: false, error: error.response?.data?.detail || 'Erreur' });
      }
      
      // Small delay to avoid overwhelming the API
      if (i < combinations.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    setGenerating(false);
    setGenerationProgress({ current: 0, total: 0, status: '' });
    
    const successCount = results.filter(r => r.success).length;
    toast.success(`${successCount}/${combinations.length} pages générées avec succès`);
    
    setGenerationResults({ open: true, results });
    loadPages();
    
    // Clear selections
    setSelectedServices([]);
    setSelectedCities([]);
  };

  const togglePublish = async (page) => {
    try {
      await api.put(`/cms/seo-pages/${page.id}`, { is_published: !page.is_published });
      toast.success(page.is_published ? 'Page dépubliée' : 'Page publiée');
      loadPages();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deletePage = async (pageId) => {
    if (!window.confirm('Supprimer cette page ?')) return;
    try {
      await api.delete(`/cms/seo-pages/${pageId}`);
      toast.success('Page supprimée');
      loadPages();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const combinationsCount = selectedServices.length * selectedCities.length;
  const existingCombinations = selectedServices.reduce((count, service) => {
    return count + selectedCities.filter(city => 
      pages.some(p => p.service === service && p.city === city)
    ).length;
  }, 0);
  const newCombinations = combinationsCount - existingCombinations;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Générateur de Pages SEO</h1>
          <p className="text-slate-500">
            Créez des pages optimisées pour le référencement local (service + ville)
          </p>
        </div>
        <Badge variant="outline" className="w-fit">
          {pages.length} pages générées
        </Badge>
      </div>

      {/* Multi-Selection Generator Card */}
      <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-500" />
            Génération en masse
          </CardTitle>
          <CardDescription>
            Sélectionnez plusieurs services et villes pour générer automatiquement toutes les combinaisons
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Services Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Services ({selectedServices.length})</Label>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={selectAllServices}>Tout</Button>
                  <Button variant="ghost" size="sm" onClick={deselectAllServices}>Aucun</Button>
                </div>
              </div>
              <div className="border rounded-lg p-3 max-h-64 overflow-y-auto space-y-2 bg-white">
                {SERVICES.map(service => (
                  <label 
                    key={service} 
                    className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer"
                  >
                    <Checkbox 
                      checked={selectedServices.includes(service)}
                      onCheckedChange={() => toggleService(service)}
                    />
                    <span className="text-sm">{service}</span>
                    {pages.some(p => p.service === service) && (
                      <Badge variant="secondary" className="text-xs ml-auto">
                        {pages.filter(p => p.service === service).length}
                      </Badge>
                    )}
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <Input 
                  placeholder="Service personnalisé..." 
                  value={customService}
                  onChange={(e) => setCustomService(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addCustomService()}
                />
                <Button variant="outline" size="icon" onClick={addCustomService}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Cities Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Villes ({selectedCities.length})</Label>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={selectAllCities}>Tout</Button>
                  <Button variant="ghost" size="sm" onClick={deselectAllCities}>Aucun</Button>
                </div>
              </div>
              <div className="border rounded-lg p-3 max-h-64 overflow-y-auto space-y-2 bg-white">
                {CITIES.map(city => (
                  <label 
                    key={city} 
                    className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer"
                  >
                    <Checkbox 
                      checked={selectedCities.includes(city)}
                      onCheckedChange={() => toggleCity(city)}
                    />
                    <span className="text-sm">{city}</span>
                    {pages.some(p => p.city === city) && (
                      <Badge variant="secondary" className="text-xs ml-auto">
                        {pages.filter(p => p.city === city).length}
                      </Badge>
                    )}
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <Input 
                  placeholder="Ville personnalisée..." 
                  value={customCity}
                  onChange={(e) => setCustomCity(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addCustomCity()}
                />
                <Button variant="outline" size="icon" onClick={addCustomCity}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Summary */}
          {combinationsCount > 0 && (
            <div className="p-4 bg-white rounded-lg border space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Combinaisons possibles</span>
                <span className="font-semibold">{combinationsCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Déjà existantes</span>
                <span className="text-amber-600">{existingCombinations}</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="font-medium text-green-700">Pages à générer</span>
                <span className="font-bold text-green-700">{newCombinations}</span>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          {generating && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{generationProgress.status}</span>
                <span className="font-medium">{generationProgress.current}/{generationProgress.total}</span>
              </div>
              <Progress value={(generationProgress.current / generationProgress.total) * 100} />
            </div>
          )}

          <Button 
            onClick={generatePages} 
            disabled={generating || newCombinations === 0}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-600"
            size="lg"
          >
            {generating ? (
              <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Zap className="w-5 h-5 mr-2" />
            )}
            Générer {newCombinations} page{newCombinations > 1 ? 's' : ''} SEO
          </Button>
        </CardContent>
      </Card>

      {/* Existing Pages */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-cyan-500" />
            Pages générées ({pages.length})
          </CardTitle>
          <CardDescription>
            Gérez vos pages SEO locales existantes
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pages.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Globe className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Aucune page SEO générée</p>
              <p className="text-sm">Sélectionnez des services et villes ci-dessus pour commencer</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {pages.map(page => (
                <div 
                  key={page.id} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={cn(
                      "w-2 h-2 rounded-full flex-shrink-0",
                      page.is_published ? "bg-green-500" : "bg-amber-500"
                    )} />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {page.service} à {page.city}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        /site/seo/{page.slug}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setPreviewModal({ open: true, page })}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => togglePublish(page)}
                    >
                      {page.is_published ? (
                        <XCircle className="w-4 h-4 text-amber-500" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => deletePage(page.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generation Results Modal */}
      <Dialog open={generationResults.open} onOpenChange={(open) => setGenerationResults({ ...generationResults, open })}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Résultats de la génération
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {generationResults.results.map((result, idx) => (
              <div 
                key={idx} 
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg",
                  result.success ? "bg-green-50" : "bg-red-50"
                )}
              >
                {result.success ? (
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm">
                    {result.service} à {result.city}
                  </p>
                  {!result.success && (
                    <p className="text-xs text-red-600">{result.error}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setGenerationResults({ open: false, results: [] })}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={previewModal.open} onOpenChange={(open) => setPreviewModal({ ...previewModal, open })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {previewModal.page?.title}
            </DialogTitle>
          </DialogHeader>
          {previewModal.page && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge>{previewModal.page.service}</Badge>
                <Badge variant="outline">
                  <MapPin className="w-3 h-3 mr-1" />
                  {previewModal.page.city}
                </Badge>
                <Badge variant={previewModal.page.is_published ? "default" : "secondary"}>
                  {previewModal.page.is_published ? "Publiée" : "Brouillon"}
                </Badge>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">Meta Title</h3>
                <p className="text-slate-700">{previewModal.page.meta_title}</p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">Meta Description</h3>
                <p className="text-slate-700">{previewModal.page.meta_description}</p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">Contenu</h3>
                <div 
                  className="prose prose-sm max-w-none" 
                  dangerouslySetInnerHTML={{ __html: previewModal.page.content }}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            {previewModal.page && (
              <Button variant="outline" asChild>
                <a href={`/site/seo/${previewModal.page.slug}`} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Voir la page
                </a>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSSeoGeneratorPage;
